import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileTabsViewer } from '@localagent/ui';
import type { WorkspaceRoutingClass } from '@localagent/sdk';
import { useWorkspaceFileBrowser } from '../../hooks/useWorkspaceFileBrowser';
import { useWorkspaceNodeActions } from '../../hooks/useWorkspaceNodeActions';
import { useWorkspaceFilesChanged } from '../../hooks/useWorkspaceFilesChanged';
import { useCloudFilesChanged } from '../../hooks/useCloudFilesChanged';
import { useFilesTreeWidthPreference } from '../../hooks/useUserPreferences';
import { useTreeColumnResize } from '../../hooks/useTreeColumnResize';
import { WorkspaceDirectoryTree } from './WorkspaceDirectoryTree';
import { WorkspaceFileContextMenu } from './WorkspaceFileContextMenu';
import { NewFileAction } from './NewFileAction';
import { FileHistoryButton } from './FileHistoryButton';

/**
 * The chat view's file surface WITHOUT the chat:
 * the same `useWorkspaceFileBrowser` + `WorkspaceDirectoryTree` +
 * `FileTabsViewer` composition `ChatWorkspaceLayout` mounts, for a standalone
 * host (the workspace detail page's Files tab). Same editing model (dirty
 * markers, CAS saves, autosave preference, conflict flow), same live-refresh
 * triggers (local watcher + cloud push), same not-ready affordances — because
 * it is the same code.
 *
 * Deliberately NOT here: chat framing (`chat`/`header` slots, panel
 * position/resize prefs), mention drag sources, and the turn-end refresh
 * handle — those are chat couplings with no meaning on a standalone page.
 */
export function WorkspaceFilesPanel({
  workspaceId,
  workspacePath,
  mode,
}: {
  workspaceId: string;
  /** Filesystem root of a LOCAL workspace — the daemon's `workspaceRoot`. */
  workspacePath?: string;
  /** Workspace transport; `undefined` while the workspace load resolves. */
  mode?: WorkspaceRoutingClass;
}) {
  const { filesTreeWidth, setFilesTreeWidth } = useFilesTreeWidthPreference();
  const resize = useTreeColumnResize({
    persistedWidth: filesTreeWidth,
    onCommitWidth: setFilesTreeWidth,
  });
  const fb = useWorkspaceFileBrowser({ workspaceId, workspacePath, mode });
  const { hasDirty, lastOwnSaveAtRef, recheckActiveFile, refreshTree } = fb;
  const nodeActions = useWorkspaceNodeActions({ fb, onOpenFile: fb.openFile });

  // `?path=` opens one file directly, so a link from elsewhere in the app — the
  // route a refused comparison offers — lands on the file itself
  // rather than on an empty viewer. Honoured once per requested path.
  const [searchParams] = useSearchParams();
  const requestedPath = searchParams.get('path');
  const openedRequestRef = useRef<string | null>(null);
  const { openFile } = fb;
  useEffect(() => {
    if (!requestedPath || openedRequestRef.current === requestedPath) return;
    openedRequestRef.current = requestedPath;
    openFile(requestedPath);
  }, [requestedPath, openFile]);

  // Exit guard (same as ChatWorkspaceLayout): armed ONLY while a tab is
  // dirty. On web this is the native leave-page prompt; on desktop Electron
  // routes the blocked unload through the main process's `will-prevent-unload`
  // handler.
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome requires returnValue to be set for the prompt to appear.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasDirty]);

  // Local watcher trigger: tree refresh + the active-tab fast path,
  // exactly as the chat layout wires it. Enabled only for local workspaces.
  const onWatcherFilesChanged = useCallback(() => {
    void recheckActiveFile();
    void refreshTree();
  }, [recheckActiveFile, refreshTree]);
  useWorkspaceFilesChanged({
    workspaceId,
    enabled: mode === 'local',
    onFilesChanged: onWatcherFilesChanged,
  });

  // Cloud push trigger: refresh on workspace.filesChanged frames, with
  // the hook's debounce + self-echo suppression via lastOwnSaveAtRef.
  const onCloudFilesChanged = useCallback(() => {
    void refreshTree();
  }, [refreshTree]);
  useCloudFilesChanged({
    workspaceId,
    enabled: mode === 'cloud',
    lastOwnSaveAtRef,
    onFilesChanged: onCloudFilesChanged,
  });

  // Full-height flex: resizable tree column, viewer takes the rest.
  return (
    <div
      className="flex h-full min-h-0 min-w-0"
      data-testid="workspace-files-panel"
      data-resizing={String(resize.isResizing)}
    >
      <div
        data-testid="workspace-files-panel-tree"
        className="relative flex-shrink-0 border-r border-border"
        style={{ width: resize.size }}
      >
        <div
          data-testid="workspace-files-panel-resize-handle"
          onPointerDown={resize.onHandlePointerDown}
          title="Resize tree"
          aria-label="Resize tree"
          className="absolute inset-y-0 right-0 z-10 w-1 cursor-col-resize touch-none select-none hover:bg-primary/40 active:bg-primary/60"
        />
        <WorkspaceDirectoryTree
          rootEntries={fb.rootEntries}
          loadChildren={fb.loadChildren}
          notReady={fb.notReady}
          resume={fb.resume}
          activePath={fb.activePath}
          onOpenFile={fb.openFile}
          onNodeContextMenu={nodeActions.openAt}
          onBackgroundContextMenu={nodeActions.openRootAt}
          onRefresh={() => void fb.refreshTree({ manual: true })}
          refreshing={fb.refreshing}
          refreshError={fb.refreshError}
          actionError={nodeActions.actionError}
          refreshRef={fb.treeRefreshRef}
          headerActions={<NewFileAction onCreateFile={(name) => fb.createFile(name, '')} />}
        />
        <WorkspaceFileContextMenu actions={nodeActions} />
      </div>
      <div data-testid="workspace-files-panel-content" className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1">
          <FileTabsViewer
            openPaths={fb.openPaths}
            activePath={fb.activePath}
            onActivate={fb.setActive}
            onClose={fb.closeFile}
            read={fb.read}
            activeSizeBytes={fb.activeSize}
            onOpenExternally={fb.openExternal}
            editing={fb.editing}
            toolbar={fb.activePath ? <FileHistoryButton workspaceId={workspaceId} path={fb.activePath} /> : undefined}
          />
        </div>
      </div>
    </div>
  );
}
